<template>
  <div class="page">
    <!-- 已绑定设备：直接进入已连接状态（04-connect-success） -->
    <template v-if="deviceHadConnect">
      <div class="countdown">设备已连接，<b>{{ countDown }}</b> 秒后进入首页</div>

      <div class="success-stage">
        <div class="success-circle">
          <svg width="140" height="140" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5.5 5.5L20 6.5"/></svg>
        </div>
        <div class="success-title">设备连接成功</div>
      </div>

      <div class="wizard-actions">
        <button class="ta-btn ghost" @click="doReset">重新激活</button>
        <button class="ta-btn primary" @click="enterHomePage">进入首页</button>
      </div>
    </template>

    <!-- 连接向导（01 欢迎 / 02 授权码 / 03 完成） -->
    <template v-else>
      <div class="countdown" v-if="step === 3">设备已连接，<b>{{ countDown }}</b> 秒后进入首页</div>

      <div class="stepper">
        <div class="track"><div class="fill" :style="{width: fillPercent}"></div></div>
        <div class="step" :class="{done: step > 1, active: step === 1}">
          <div class="dot">
            <svg v-if="step > 1" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5.5 5.5L20 6.5"/></svg>
            <template v-else>1</template>
          </div>
          <div class="label">开始</div>
        </div>
        <div class="step" :class="{done: step > 2, active: step === 2}">
          <div class="dot">
            <svg v-if="step > 2" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5.5 5.5L20 6.5"/></svg>
            <template v-else>2</template>
          </div>
          <div class="label">设备连接</div>
        </div>
        <div class="step" :class="{active: step === 3}">
          <div class="dot">3</div>
          <div class="label">完成</div>
        </div>
      </div>

      <div class="wizard-stage">
        <!-- step 1：设备信息确认（免授权码静默重连中仅显示提示） -->
        <div class="wizard-card" v-if="step === 1">
          <div v-if="silentConnecting" class="connecting-tip">设备自动连接中…</div>
          <template v-else>
            <div class="wizard-title">访客终端设备欢迎您</div>
            <div class="field-row">
              <span class="field-label">设备型号</span>
              <input class="ta-input" :value="baseInfo ? baseInfo.model : ''" disabled>
            </div>
          </template>
        </div>

        <!-- step 2：输入授权码连接（授权码由平台总后台生成并分配给租户，绑定后与设备 1:1） -->
        <div class="wizard-card" v-else-if="step === 2">
          <div v-if="connectInfo.isConnecting" class="connecting-tip">设备连接中…</div>
          <div class="field-row" v-else>
            <span class="field-label">授权码</span>
            <input class="ta-input" v-model="connectInfo.code" placeholder="平台分配给本企业的设备授权码">
          </div>
        </div>

        <!-- step 3：完成 -->
        <div class="wizard-card" v-else-if="step === 3">
          <div class="field-row">
            <span class="field-label">授权码</span>
            <input class="ta-input" :value="baseInfo.code" disabled>
          </div>
        </div>
      </div>

      <div class="wizard-actions">
        <button class="ta-btn ghost" :disabled="!lastButtonEnable" @click="doLast">上一步</button>
        <button class="ta-btn primary" :disabled="!nextButtonEnable || connectInfo.isConnecting || silentConnecting" @click="doNext">
          {{ connectInfo.isConnecting && step === 2 ? "设备连接中…" : "下一步" }}
        </button>
      </div>

      <my-notify-dialog :show="notify.show" :success="notify.success" :msg="notify.msg" :title="notify.title"
                        @dialogClose="notify.show = false"></my-notify-dialog>
    </template>
  </div>
</template>

<script>
import {addDevice, reconnectDevice} from "@/api/device-api";
import MyNotifyDialog from "@/components/MyNotifyDialog.vue";
import {mapMutations} from 'vuex'
import {getDeviceInfo, getDeviceFingerprint, resolveDeviceFingerprint} from "@/utils/native-bridge";

export default {
  name: "LoadingPage",
  components: {MyNotifyDialog},

  computed: {
    // 步骤条进度：step1=0% / step2=50% / step3=100%
    fillPercent() {
      if (this.step === 1) return "0%";
      if (this.step === 2) return "50%";
      return "100%";
    },
  },

  mounted() {
    this.doGetBaseInfoAction()
  },

  data() {
    return {
      baseInfoKeys: [
        "code", "areaId", "token", "connect", "deviceId"
      ],

      step: 1,
      currentStatus: "process",
      deviceHadConnect: false,
      lastButtonEnable: false,
      nextButtonEnable: false,

      // 免授权码静默重连进行中（隐藏向导内容、禁用下一步）
      silentConnecting: false,
      // 重新激活后置位：跳过自动重连，允许手动换授权码
      reconnectBlocked: false,

      notify: {show: false, success: false, msg: "", title: "",},

      baseInfo: null,

      connectInfo: {
        isFinish: false,
        isSuccess: false,
        isConnecting: false,
        code: "",
      },

      countDown: 15,
      timer: null,
    }
  },

  methods: {
    ...mapMutations(['setToken', 'setAreaId', "setDeviceId"]),

    doLast() {
      if (this.timer) {
        clearInterval(this.timer)
      }
      if (this.step === 3) {
        this.step = 2
        this.currentStatus = "process"
        this.nextButtonEnable = true
        this.connectInfo = {isFinish: false, isSuccess: false, isConnecting: false, code: ""}
      } else if (this.step === 2) {
        this.step = 1
        this.currentStatus = "process"
        this.nextButtonEnable = true
      }
    },

    doNext() {
      if (this.step === 1) {
        this.stepToNext()
      } else if (this.step === 2) {
        this.doConnect()
      } else if (this.step === 3) {
        this.enterHomePage()
      }
    },

    doGetBaseInfoAction() {
      this.nextButtonEnable = false
      this.lastButtonEnable = false

      let baseInfo = {}
      this.baseInfoKeys.forEach((key) => {
        this.$set(baseInfo, key, this.JavaBaseInfo.getBaseInfo(key))
      })
      // 设备型号：浏览器/壳内均由 UA 实时解析（老版从安卓壳读取，H5 版无壳）
      this.$set(baseInfo, "model", getDeviceInfo().model)
      console.log("doGetBaseInfo from localStorage", baseInfo)
      this.baseInfo = baseInfo

      if (baseInfo.token && baseInfo.connect === "true") {
        // 已绑定设备：直接进入已连接状态（token 失效时由 http 拦截器清空并跳回本页）
        this.doShowAlreadyConnect(baseInfo)
      } else {
        this.step = 1
        this.nextButtonEnable = true
        this.lastButtonEnable = false
        this.doTryReconnect()
      }
    },

    // 免授权码静默重连：本地有硬件指纹（此前授权码连接成功过）时凭指纹补发 token，
    // 覆盖重启 / 30 天 token 过期 / 重装后 WebView 数据被清（指纹丢失自然回退向导）
    async doTryReconnect() {
      if (this.reconnectBlocked) return
      const fp = await resolveDeviceFingerprint()
      if (!fp || this.reconnectBlocked) return
      this.silentConnecting = true
      const dev = getDeviceInfo()
      reconnectDevice({
        hwSn: fp,
        model: dev.model + "|" + dev.screen.width + "x" + dev.screen.height,
      }).then((res) => {
        const data = res.data
        if (data.code === 200 && data.data && data.data.token) {
          this.silentConnecting = false
          this.saveDataToLocalStorage(data.data)
          this.doShowAlreadyConnect()
        } else {
          this.silentConnecting = false
        }
      }).catch(() => {
        this.silentConnecting = false
      })
    },

    stepToNext() {
      this.step++
      this.currentStatus = "process"
    },

    async doConnect() {
      if (this.connectInfo.code === "") {
        this.$message.error("请输入授权码")
        return
      }

      this.connectInfo.isConnecting = true
      this.nextButtonEnable = false
      this.lastButtonEnable = false

      // 上报真实设备信息：机型取 UA 解析（壳内为平板实际机型），分辨率取物理像素，
      // hwSn 用稳定设备指纹顶替（壳内=系统级 ANDROID_ID，浏览器=持久化随机串）。形如 SM-X200|2560x1600
      const dev = getDeviceInfo()
      const fp = await resolveDeviceFingerprint()
      let deviceInfo = {
        code: this.connectInfo.code,
        category: "VISIT_MACHINE",
        type: "PAD",
        model: dev.model + "|" + dev.screen.width + "x" + dev.screen.height,
        hwSn: fp,
        wifiMac: "",
      }

      addDevice(deviceInfo).then((res) => {
        let data = res.data
        this.connectInfo.isConnecting = false
        console.log("addDevice result " + JSON.stringify(data))
        if (data.code === 200 && data.data && data.data.token) {
          this.doOnConnectSuccess(data.data)
        } else {
          this.connectInfo.isFinish = true
          this.connectInfo.isSuccess = false
          this.notify.show = true
          this.notify.success = false
          this.notify.msg = (data && data.msg) ? data.msg : "授权码无效或未分配"
          this.notify.title = "连接失败"
          this.currentStatus = "error"
          this.nextButtonEnable = true
          this.lastButtonEnable = true
        }
      }).catch((error) => {
        console.log("addDevice", error)
        this.connectInfo.isConnecting = false
        this.connectInfo.isFinish = true
        this.connectInfo.isSuccess = false
        this.notify.show = true
        this.notify.success = false
        this.notify.msg = "连接失败，请检查网络与服务地址"
        this.notify.title = "连接失败"
        this.currentStatus = "error"
        this.nextButtonEnable = true
        this.lastButtonEnable = true
      })
    },

    doOnConnectSuccess(device) {
      this.baseInfo.code = this.connectInfo.code

      this.nextButtonEnable = true
      this.currentStatus = "success"
      this.step = 3
      this.connectInfo.isFinish = true
      this.connectInfo.isSuccess = true

      this.countDownFunc()
      this.saveDataToLocalStorage(device)
    },

    saveDataToLocalStorage(device) {
      this.baseInfo.code = this.connectInfo.code
      this.baseInfo.areaId = device.areaId
      this.baseInfo.token = device.token
      this.baseInfo.deviceId = device.id
      this.baseInfo.connect = "true"
      this.setAreaId(this.baseInfo.areaId)
      this.setDeviceId(this.baseInfo.deviceId)
      this.setToken(this.baseInfo.token)

      this.baseInfoKeys.forEach((key) => {
        this.JavaBaseInfo.setBaseInfo(key, this.baseInfo[key])
      })
    },

    doShowAlreadyConnect() {
      this.deviceHadConnect = true
      this.countDownFunc()
      this.setToken(this.baseInfo.token)
      this.setAreaId(this.baseInfo.areaId)
      this.setDeviceId(this.baseInfo.deviceId)
    },

    countDownFunc() {
      this.countDown = 15
      this.timer = setInterval(() => {
        this.countDown--
        if (this.countDown === 0) {
          clearInterval(this.timer)
          this.$router.push({name: this.myPath.home})
        }
      }, 1000)
    },
    enterHomePage() {
      if (this.timer) {
        clearInterval(this.timer)
      }
      this.$router.push({name: this.myPath.home})
    },

    doReset() {
      this.step = 1
      this.deviceHadConnect = false
      this.currentStatus = "process"

      this.connectInfo.isFinish = false
      this.connectInfo.isSuccess = false
      this.connectInfo = {isFinish: false, isSuccess: false, isConnecting: false, code: ""}

      // 重新绑定：清空本地设备会话，并跳过自动重连（允许手动换授权码）
      this.reconnectBlocked = true
      this.setToken('')
      this.setAreaId('')
      this.setDeviceId('')
      this.JavaBaseInfo.setBaseInfo("token", "")
      this.JavaBaseInfo.setBaseInfo("connect", "false")

      this.doGetBaseInfoAction()
      if (this.timer) {
        clearInterval(this.timer)
      }
    },
  }
}

</script>

<style scoped lang="less">
.page {
  background: var(--bg);
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

// ---------- 步骤条（theme-a stepper 定义于 App.vue 全局） ----------

// ---------- 向导卡区 ----------
.wizard-stage {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wizard-card {
  width: 1240px;
  padding: 110px 120px;
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-card);
}

.wizard-title {
  font-size: 64px;
  font-weight: 700;
  line-height: 1.5;
  margin-bottom: 72px;
}

.connecting-tip {
  font-size: 40px;
  color: var(--ink-2);
  text-align: center;
}

// ---------- 底部按钮 ----------
.wizard-actions {
  flex: none;
  display: flex;
  justify-content: center;
  gap: 56px;
  padding-bottom: 120px;
}

// ---------- 已连接态（蓝圆对勾） ----------
.success-stage {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 80px;

  .success-circle {
    width: 280px;
    height: 280px;
    border-radius: 50%;
    background: var(--blue);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 24px 60px rgba(47, 107, 255, 0.35);

    svg {
      width: 140px;
      height: 140px;
    }
  }

  .success-title {
    font-size: 96px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }
}
</style>
